import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { useFringeQuery } from 'utils/api/queries';
import PropTypes from 'prop-types';
import { toastStyle } from 'utils/helper';
import { useLocation } from 'react-router-dom';
import FormSelect from 'components/FormSelect';
import { components } from 'react-select';

const EditGroup = props => {
  const {
    UpdateGroupsMutation,
    refetchGroup,
    hideUpdateGroupModal,
    selectTab,
    clearBudgetRename,
    activeBudget,
    hasEditAccess,
  } = useHomeScreenContext();

  const [fringeList, setFringeList] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';

  const { data: groupDetails } = useFringeQuery.useFringeDetailsQuery({
    filters: {
      instance_type: instanceType,
      active_id: activeBudget,
    },
    enabled: true,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    control,
  } = useForm({
    defaultValues: {
      name: props?.GroupName || '',
      id: props?.GroupId,
      fringe: props?.fringe || '',
      fringes: [],
    },
  });

  const { setshowCreateGroupsModal } = useHomeScreenContext();

  const CustomMenuList = ({ children, ...rest }) => {
    return (
      <components.MenuList {...rest}>
        {children}
        <div
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
          }}
          onClick={() => setshowCreateGroupsModal(true)}
        >
          + Add Group
        </div>
      </components.MenuList>
    );
  };
  CustomMenuList.propTypes = {
    children: PropTypes.node.isRequired,
  };

  useEffect(() => {
    const fringeNames =
      props?.GroupFringes?.split(',')
        ?.map(f => f.trim())
        ?.filter(Boolean) || [];

    const allFringes = groupDetails?.data?.data || [];

    const isReceivingIds =
      fringeNames.length > 0 && !isNaN(parseInt(fringeNames[0]));

    let matchedFringes;
    if (isReceivingIds) {
      matchedFringes = allFringes.filter(fringe =>
        fringeNames.includes(String(fringe.id))
      );
    } else {
      matchedFringes = allFringes.filter(fringe =>
        fringeNames.includes(fringe.display_name)
      );
    }

    setFringeList(matchedFringes);

    const defaultFringes = matchedFringes.map(fringe => ({
      value: fringe.id,
      label: fringe.display_name,
    }));
    setValue('fringes', defaultFringes);
  }, [groupDetails, props.GroupFringes, activeBudget, setValue]);

  const handleCreateBudget = handleSubmit(async data => {
    try {
      const selectedFringeIds = data.fringes
        ? data.fringes.map(option => option.value)
        : [];
      const originalFringeIds = fringeList.map(fringe => fringe.id);

      const fringesToAdd = selectedFringeIds.filter(
        id => !originalFringeIds.includes(id)
      );
      const fringesToRemove = originalFringeIds.filter(
        id => !selectedFringeIds.includes(id)
      );

      const response = await UpdateGroupsMutation.mutateAsync({
        ...data,
        id: props?.GroupId,
        add_fringe: fringesToAdd,
        remove_fringe: fringesToRemove,
        instance_type: instanceType,
      });

      const newBudget = response?.data;
      if (newBudget?.id) selectTab(newBudget.id, newBudget);
      refetchGroup?.();
      toast.success(`Budget updated successfully`, toastStyle);
      reset();
      handleCancel();
      props?.refetch();
    } catch (error) {
      let message = `An error occurred while updating the budget. Please try again later`;

      if (error?.response?.data) {
        const responseData = error.response.data;
        if (responseData?.name) {
          message = responseData.name;
        } else if (responseData?.non_field_errors?.[0]) {
          message = responseData.non_field_errors[0];
        } else if (responseData?.error) {
          message = responseData.error;
        }
      }

      toast.error(message, toastStyle);
      console.error('Error updating fringe group:', error);
    }
  });

  const handleCancel = () => {
    hideUpdateGroupModal();
    clearBudgetRename();
    reset();
    props?.closeFolder?.();
  };

  return (
    <Modal
      onHide={handleCancel}
      show={props?.showCreateGroupsModal}
      size="sm"
      className="create-budget"
      bodyClass="create-budget__body"
      centered
    >
      <h1 className="create-budget__body__header">
        Edit Groups{' '}
        {watch('name') && (
          <span className="budget-name">&quot;{watch('name')}&quot;</span>
        )}
      </h1>
      <Form className="create-budget__body__form" onSubmit={handleCreateBudget}>
        <FormInput
          label="Group Name"
          type="text"
          error={errors?.name?.message}
          placeholder="Please enter a name"
          {...register('name', { required: 'Group name is required' })}
          disabled={instanceType === 'budget' && !hasEditAccess()}
        />

        <div className="group-select-container">
          <FormSelect
            label="Fringes"
            placeholder="Select fringes"
            options={(() => {
              const selectedFringes = watch('fringes') || [];
              const selectedIds = selectedFringes.map(f => f.value);
              return (
                groupDetails?.data?.data
                  ?.filter(fringe => !selectedIds.includes(fringe.id))
                  ?.map(fringe => ({
                    value: fringe.id,
                    label: fringe.display_name,
                  })) || []
              );
            })()}
            name="fringes"
            control={control}
            className="mb-5"
            isMulti={true}
            menuPlacement="top"
            menuPortalTarget={document.body}
            // components={{ MenuList: CustomMenuList }}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              menu: base => ({
                ...base,
                height: '200px',
                overflowY: 'auto',
              }),
              menuList: base => ({
                ...base,
                maxHeight: '200px',
                overflowY: 'auto',
              }),
              control: base => ({
                ...base,
                minHeight: '40px',
              }),
            }}
            isDisabled={instanceType === 'budget' && !hasEditAccess()}
          />
        </div>

        <div className="create-budget__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="create-budget__action__cancel"
            variant="red-outlined"
            onClick={handleCancel}
          />
          <Button
            label="Confirm"
            className="create-budget__action__confirm"
            type="submit"
            isDisabled={instanceType === 'budget' && !hasEditAccess()}
          />
        </div>
      </Form>
    </Modal>
  );
};

EditGroup.propTypes = {
  GroupId: PropTypes.number,
  showCreateGroupsModal: PropTypes.bool,
  fringe: PropTypes.string,
  GroupFringes: PropTypes.string,
  GroupName: PropTypes.string,
  closeFolder: PropTypes.func,
  refetch: PropTypes.func.isRequired,
};

EditGroup.defaultProps = {
  showCreateGroupsModal: false,
  closeFolder: undefined,
  GroupId: '',
  GroupName: '',
  fringe: '',
  GroupFringes: '',
};

export default EditGroup;
